import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { roundDone } from "./useRoundLock";
import {
  formatDraftWeekLine,
  programRoundLabel,
  roundTypeAt,
  type DraftLineOptions,
  type RoundType,
  type SlotDraft,
  type UnitsPreference,
} from "@heraion/shared";

import { Badge } from "../primitives/Badge";
import { Button } from "../primitives/Button";
import type { UiTheme } from "../theme";
import { RoundLineRow } from "./RoundLineRow";
import { RoundSetsEditor } from "./RoundSetsEditor";
import { RoundTypeBadge, useRoundTypeWords } from "../primitives/RoundTypeBadge";
import type { V3ExerciseActions } from "./useV3ExerciseActions";
import { NEXT_BLOCK_ROUNDS } from "./useV3ProgressionActions";
import { DoneCheck, doneWord } from "./useWeekMark";
import type { V3Styles } from "./v3Styles";

// The round ladder of one exercise (#336, brief §7.2 «Scala»): one 44pt row
// per round with that round's line, and the open one edits its sets. A round
// the exercise does not run in says so, opens nothing, and offers «+» to put
// the exercise in it (3b), seeded from its nearest round. A deload or test
// round carries its badge under the W (4d). Slice 5: a round with cells by
// hand says «✎ by hand»; open, it offers «Realign W<n>» (not in a round
// already done) and says that a typed value stays. A round at effort or at %
// shows its reference load, read only. A round already done (decision 4) is
// marked «✓ done» and opens read only, its reason in words and spoken; one the
// exercise does not run in says why it has no «+». Under the last round,
// «+ 4 rounds». Without `actions` (#358) every row is read only:
// the round open from the grid is marked, nothing opens or is added, and a
// round done is a bare «✓», and «by hand» takes the grid's ring, not the pencil.
export function RoundLadder({
  theme,
  styles,
  slot,
  slotIdx,
  cycleWeeks,
  roundTypes,
  expandedRound,
  onToggleRound,
  lineOptions,
  cellUnit,
  actions,
  hand,
  onRealignRound,
  onAddBlock,
  refs,
  lockedThrough = -1,
}: {
  theme: UiTheme;
  styles: V3Styles;
  slot: SlotDraft;
  slotIdx: number;
  cycleWeeks: number;
  /** Draft.roundTypes: absent, every round is normal. */
  roundTypes?: readonly RoundType[];
  expandedRound: number | null;
  onToggleRound?: (w: number) => void;
  lineOptions: DraftLineOptions;
  cellUnit: UnitsPreference;
  actions?: V3ExerciseActions;
  /** The rounds with cells by hand (the slot's rule exceptions). */
  hand?: ReadonlySet<number>;
  onRealignRound?: (w: number) => void;
  /** «+ 4 rounds»; absent when the cycle has no room. */
  onAddBlock?: () => void;
  /** Round by round, «ref. ≈ X kg» or null (no MAX, no reference). */
  refs?: ReadonlyArray<string | null>;
  /** Rounds 0..lockedThrough were executed: no «Realign» there. */
  lockedThrough?: number;
}) {
  const { t } = useTranslation();
  const handWord = t("editorV3.rule.byHandMark", { defaultValue: "by hand" });
  const off = t("editorV3.notInRound", { defaultValue: "Not in this round" });
  const offDone = t("editorV3.lock.offDone", {
    defaultValue: "Not in this round, and it is done: unlock at the top to add the exercise.",
  });
  const words = useRoundTypeWords();
  return (
    <View style={styles.ladder}>
      {Array.from({ length: cycleWeeks }, (_, w) => {
        const line = formatDraftWeekLine(slot, w, lineOptions);
        const open = expandedRound === w && line !== null;
        const round = programRoundLabel(w + 1);
        const type = roundTypeAt({ roundTypes }, w);
        const ref = line === null ? null : (refs?.[w] ?? null);
        const done = roundDone(w, lockedThrough);
        // A done round takes no «+»: its words say why.
        const offText = done && actions ? offDone : off;
        const said =
          type === "normal"
            ? t("editorV3.roundA11y", { n: w + 1, line: line ?? offText, defaultValue: "Round {{n}}: {{line}}" })
            : t("editorV3.roundTypedA11y", {
                n: w + 1,
                type: words[type],
                line: line ?? offText,
                defaultValue: "Round {{n}}, {{type}}: {{line}}",
              });
        const spoken = [said, ref, done ? t("editorV3.lock.doneA11y", { defaultValue: "done, read only" }) : null]
          .filter(Boolean)
          .join(", ");
        return (
          <View key={w}>
            <RoundLineRow
              theme={theme}
              styles={styles}
              variant="ladder"
              label={round}
              badge={
                type === "normal" && !hand?.has(w) && !ref && !done ? undefined : (
                  <>
                    {!done ? null : actions ? <Badge theme={theme} icon="check" label={doneWord(t)} /> : <DoneCheck theme={theme} />}
                    {type === "normal" ? null : <RoundTypeBadge theme={theme} type={type} />}
                    {hand?.has(w) ? <Badge theme={theme} icon={actions ? "edit" : "circle"} label={handWord} /> : null}
                    {ref ? <Text style={styles.line}>{ref}</Text> : null}
                  </>
                )
              }
              line={line}
              offText={offText}
              open={open}
              // Read only, but still reachable by a screen reader.
              onPress={line === null || !onToggleRound ? undefined : () => onToggleRound(w)}
              a11yLabel={spoken}
              onAdd={done || !actions ? undefined : () => actions.setRoundActive(slotIdx, w, true)}
              addA11yLabel={t("editorV3.addToRound", { n: w + 1, defaultValue: "Add to W{{n}}" })}
            />
            {!open || !actions ? null : done ? (
              <View
                style={styles.expanded}
                accessible
                accessibilityState={{ disabled: true }}
                aria-disabled
                accessibilityLabel={`${round}: ${line}. ${t("editorV3.lock.readOnly", {
                  round,
                  defaultValue: "{{round}} is done: read only. Unlock it at the top of the editor.",
                })}`}
              >
                <Text style={styles.roundLine}>{line}</Text>
                <Text style={styles.muted}>
                  {t("editorV3.lock.readOnly", {
                    round,
                    defaultValue: "{{round}} is done: read only. Unlock it at the top of the editor.",
                  })}
                </Text>
              </View>
            ) : (
              <View style={styles.expanded}>
                <RoundSetsEditor
                  theme={theme}
                  styles={styles}
                  slot={slot}
                  slotIdx={slotIdx}
                  week={w}
                  cellUnit={cellUnit}
                  actions={actions}
                />
                {hand?.has(w) && onRealignRound && w > lockedThrough ? (
                  <Button
                    theme={theme}
                    variant="secondary"
                    numberOfLines={2}
                    label={t("editorV3.rule.realignRound", { round, defaultValue: "Realign {{round}} with the rule" })}
                    onPress={() => onRealignRound(w)}
                  />
                ) : null}
                {hand?.has(w) ? (
                  <Text style={styles.muted}>
                    {t("editorV3.rule.typedStays", {
                      defaultValue: "A value typed here stays yours: the rule does not write over it.",
                    })}
                  </Text>
                ) : null}
              </View>
            )}
          </View>
        );
      })}
      {onAddBlock ? (
        <View style={styles.expanded}>
          <Button
            theme={theme}
            variant="ghost"
            icon="plus"
            label={t("editorV3.rule.addBlock", { count: NEXT_BLOCK_ROUNDS, defaultValue: "{{count}} rounds" })}
            accessibilityLabel={t("editorV3.rule.blockAdd", { count: NEXT_BLOCK_ROUNDS, defaultValue: "Add {{count}} rounds" })}
            onPress={onAddBlock}
          />
        </View>
      ) : null}
    </View>
  );
}
